import { ComplementList, HumanResourcesItem } from '@/app/models/detailsRH';
import { useHumanResourcesComplementQuery } from '@/app/services/detailsHRApi';
import { formatCPFAndCNPJ, formatDotToComma } from '@/app/utils/numberUtils';
import DisplayAlert from '@/components/common/displayAlert';
import TruncatedTextWithTooltip from '@/components/common/truncatedTextWithTooltip';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Checkbox, IconButton, TableCell, TableRow, Tooltip, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const mainRowSx = {
  '& > *': { borderBottom: 'unset' },
  backgroundColor: 'grey.100',
  cursor: 'pointer',
};

const categoryContainerSx = {
  display: 'flex',
};

const expenseGroupTextSx = {
  fontSize: 'body2.fontSize',
};

const subRowSx = {
  backgroundColor: 'background.paper',
};

const subCellSx = {
  fontSize: 'body2.fontSize',
  backgroundColor: 'background.paper',
  pl: 5,
  pr: 2,
  py: 1,
};

const bodyCellSx = {
  fontSize: 'body2.fontSize',
  backgroundColor: 'grey.50',
};

const actionsCellSx = {
  ...bodyCellSx,
  textAlign: 'right',
  position: 'sticky',
  right: 0,
  zIndex: 2,
  boxShadow: '-2px 0 4px rgba(0, 0, 0, 0.05)',
};

const checkboxCellSx = {
  ...bodyCellSx,
  padding: 'checkbox',
  position: 'sticky',
  left: 0,
  zIndex: 2,
  boxShadow: '2px 0 4px rgba(0, 0, 0, 0.05)',
};

const nameCellSx = {
  ...bodyCellSx,
  position: 'sticky',
  zIndex: 2,
  boxShadow: '2px 0 4px rgba(0, 0, 0, 0.05)',
};

type HumanResourcesDetailTableProps = {
  row: HumanResourcesItem;
  selectHumanResource: HumanResourcesItem[];
  setSelectHumanResource: (ids: HumanResourcesItem[]) => void;
  setAnchorEl: (el: HTMLElement | null) => void;
  setSelectedStatus: (id: number | null) => void;
  setIdUndoUnion: (id: number | null) => void;
  setSelectedHumanResourceItem: (id: HumanResourcesItem) => void;
};

function HumanResourcesDetailTable({
  row,
  selectHumanResource,
  setSelectHumanResource,
  setAnchorEl,
  setSelectedStatus,
  setIdUndoUnion,
  setSelectedHumanResourceItem,
}: HumanResourcesDetailTableProps) {
  const { t } = useTranslation(['humanResource', 'common']);

  const theme = useTheme();

  const [open, setOpen] = useState(false);

  const isSelected = selectHumanResource.includes(row);

  const handleCheckboxChange = (id: HumanResourcesItem, checked: boolean) => {
    if (checked) {
      setSelectHumanResource([...selectHumanResource, id]);
    } else {
      setSelectHumanResource(selectHumanResource.filter((itemId) => itemId !== id));
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedStatus(id);
  };

  const { data } = useHumanResourcesComplementQuery(row?.id as number, {
    skip: !row || !open,
  });

  return (
    <>
      <TableRow
        sx={mainRowSx}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <TableCell sx={{ ...checkboxCellSx, padding: 'checkbox' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              color="secondary"
              sx={{ p: 0 }}
              checked={isSelected}
              onChange={(e) => handleCheckboxChange(row, e.target.checked)}
              inputProps={{ 'aria-labelledby': `Item-${row.id}` }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
        </TableCell>

        <TableCell sx={{ ...nameCellSx, minWidth: theme.spacing(40), left: theme.spacing(7) }}>
          <TruncatedTextWithTooltip value={row.name || '---'} sx={bodyCellSx} size={40} />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, minWidth: theme.spacing(18) }}>{formatCPFAndCNPJ(row.cpf)}</TableCell>

        <TableCell sx={{ ...bodyCellSx, minWidth: theme.spacing(30) }}>
          <TruncatedTextWithTooltip value={row.positionCode || '---'} sx={{ ...bodyCellSx }} size={20} />
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, minWidth: theme.spacing(30) }}>
          <TruncatedTextWithTooltip
            value={row.graduationCourseCode ? row.graduationCourseCode : '---'}
            sx={{ ...bodyCellSx, textAlign: 'left' }}
            size={20}
          />
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, minWidth: theme.spacing(25) }}>
          <TruncatedTextWithTooltip
            value={row.graduationLevel ? row.graduationLevel.name : '---'}
            sx={{ ...bodyCellSx, textAlign: 'left' }}
            size={20}
          />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center', minWidth: theme.spacing(20) }}>
          {formatDotToComma(row.monthlyJourney)}
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
          {formatDotToComma(row.ratio)}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'center', minWidth: theme.spacing(15) }}>
          {row.extraHours ? `${row.extraHours}` : '---'}
        </TableCell>

        <TableCell
          sx={{
            ...bodyCellSx,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            minWidth: theme.spacing(15),
          }}
        >
          <DisplayAlert value={row.salary} variation={row.salaryVariation} />
        </TableCell>

        <TableCell
          sx={{
            ...bodyCellSx,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            minWidth: theme.spacing(15),
          }}
        >
          <DisplayAlert value={row.awards} variation={row.awardsVariation} />
        </TableCell>

        <TableCell
          sx={{
            ...bodyCellSx,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            minWidth: theme.spacing(15),
          }}
        >
          <DisplayAlert value={row.benefits} variation={row.benefitsVariation} />
        </TableCell>

        <TableCell
          sx={{
            ...bodyCellSx,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            minWidth: theme.spacing(15),
          }}
        >
          <DisplayAlert value={row.charges} variation={row.chargesVariation} />
        </TableCell>
        <TableCell
          sx={{
            ...bodyCellSx,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            minWidth: theme.spacing(15),
          }}
        >
          <DisplayAlert value={row.chargesAward} variation={row.chargesAwardVariation} />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, borderBottom: '1px solid rgba(224, 224, 224, 1)', textAlign: 'center' }}>
          {row.consolidated ? (
            <CheckIcon sx={{ color: theme.palette.success.light, cursor: 'pointer' }} />
          ) : (
            <CloseIcon sx={{ color: theme.palette.grey[500], cursor: 'pointer' }} />
          )}
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, borderBottom: '1px solid rgba(224, 224, 224, 1)', textAlign: 'center' }}>
          {row.grouped ? (
            <Tooltip title={t('common:union.result')} arrow placement="bottom-start">
              <LinkIcon sx={{ color: theme.palette.secondary.light, cursor: 'pointer' }} />
            </Tooltip>
          ) : null}
        </TableCell>

        <TableCell sx={actionsCellSx}>
          <IconButton
            size="small"
            onClick={(e) => {
              setOpen(false);
              e.stopPropagation();
              setIdUndoUnion(row.id);
              setSelectedHumanResourceItem(row);
              handleMenuOpen(e, row.id);
            }}
            aria-label="more actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      {open &&
        data?.expenseList.map((subRow: ComplementList) => (
          <TableRow key={`${row.id}-${subRow.complement}`} sx={subRowSx}>
            <TableCell component="th" scope="row" sx={subCellSx} colSpan={17}>
              <Box sx={categoryContainerSx}>
                <Typography sx={expenseGroupTextSx}>{subRow.complement}</Typography>
              </Box>
            </TableCell>
          </TableRow>
        ))}
    </>
  );
}

export default HumanResourcesDetailTable;
